import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { NOOR } from "@/constants/testIds/noor";
import useFormSubmit from "./useFormSubmit";
import StatusMessage from "./StatusMessage";

const INITIAL = { name: "", email: "", message: "", inquiry_type: "general" };

const TYPES = [
  { value: "general", label: "General · Curiosity" },
  { value: "press", label: "Press · Editorial" },
  { value: "hospitality", label: "Hospitality · Restaurants & bars" },
  { value: "collector", label: "Private collector" },
  { value: "trade", label: "Trade · Distribution" },
];

const CONTACT_LINES = [
  { k: "Atelier", v: "Jalisco · México" },
  { k: "Office", v: "London · United Kingdom" },
  { k: "Email", v: "hello@noor.house" },
];

function ContactInfo() {
  return (
    <div className="col-span-12 lg:col-span-5">
      <p className="overline mb-4">VI · Correspondence</p>
      <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]" style={{ color: "var(--text-primary)" }}>
        Speak with <br />
        the <em className="italic" style={{ color: "var(--accent-rose)" }}>House of Noor</em>.
      </h2>
      <p className="mt-8 text-base md:text-lg font-light max-w-md" style={{ color: "var(--text-secondary)" }}>
        For collectors, hospitality partners and the curious. We respond to every letter,
        though sometimes by candlelight.
      </p>
      <div className="mt-12 space-y-5 text-sm" style={{ color: "var(--text-secondary)" }}>
        {CONTACT_LINES.map(({ k, v }) => (
          <div key={k} className="flex items-center gap-6">
            <span className="overline w-20 inline-block" style={{ fontSize: "0.62rem" }}>{k}</span>
            <span className="font-display text-lg" style={{ color: "var(--text-primary)" }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-3">{label}</label>
      {children}
    </div>
  );
}

function NameEmailRow({ form, set }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <Field label="Your name">
        <input
          data-testid={NOOR.inquiryNameInput}
          type="text"
          required
          value={form.name}
          onChange={set("name")}
          placeholder="Anaïs Devereaux"
          className="input-line"
        />
      </Field>
      <Field label="Email">
        <input
          data-testid={NOOR.inquiryEmailInput}
          type="email"
          required
          value={form.email}
          onChange={set("email")}
          placeholder="anais@maison.com"
          className="input-line"
        />
      </Field>
    </div>
  );
}

function InquiryTypeField({ value, onChange }) {
  return (
    <Field label="Nature of inquiry">
      <select
        data-testid={NOOR.inquiryTypeSelect}
        value={value}
        onChange={onChange}
        className="select-line"
      >
        {TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
    </Field>
  );
}

function MessageField({ value, onChange }) {
  return (
    <Field label="Your letter">
      <textarea
        data-testid={NOOR.inquiryMessageInput}
        required
        rows={4}
        value={value}
        onChange={onChange}
        placeholder="Tell us why Noor found you…"
        className="input-line resize-none"
      />
    </Field>
  );
}

function SubmitRow({ status, errMsg }) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-2">
      <button
        data-testid={NOOR.inquirySubmitBtn}
        type="submit"
        className="btn-solid"
        disabled={status === "loading"}
      >
        {status === "loading" ? "Sending…" : "Send letter"}
        <ArrowRight size={14} />
      </button>
      <StatusMessage
        status={status}
        errMsg={errMsg}
        successText="Received with care. We shall reply soon."
        successTestId={NOOR.inquirySuccess}
        variant="inline"
        motionPreset="fade"
      />
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const { status, errMsg, submit, reset } = useFormSubmit("/inquiries", { resetTo: INITIAL });

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (status !== "idle" && status !== "loading") reset();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submit(form, setForm);
  };

  return (
    <form onSubmit={onSubmit} className="col-span-12 lg:col-span-6 lg:col-start-7 space-y-10">
      <NameEmailRow form={form} set={set} />
      <InquiryTypeField value={form.inquiry_type} onChange={set("inquiry_type")} />
      <MessageField value={form.message} onChange={set("message")} />
      <SubmitRow status={status} errMsg={errMsg} />
    </form>
  );
}

export default function Contact() {
  return (
    <section data-testid={NOOR.contactSection} id="contact" className="relative py-32 md:py-44">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-y-10 md:gap-10">
        <ContactInfo />
        <ContactForm />
      </div>
    </section>
  );
}
